import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEvent, createOrder, createGuestOrder, createPaymentIntent, confirmPayment } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import PaymentForm from '../components/PaymentForm';
import PaymentSuccess from '../components/PaymentSuccess';
import ContactOrganizerButton from '../components/ContactOrganizerButton';
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  User,
  Ticket,
  Minus,
  Plus,
  CreditCard,
  ArrowLeft,
  Share2,
  Shield,
  Zap,
  CheckCircle,
  Clock,
  Youtube,
  Mail,
  UserCircle,
  AlertTriangle,
  Eye,
  Facebook,
  Twitter,
  MessageCircle
} from 'lucide-react';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  
  // Guest checkout state
  const [checkoutMode, setCheckoutMode] = useState(null); // 'guest' | 'register' | null
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  
  // Countdown timer state
  const [showCheckout, setShowCheckout] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  
  // Payment success state
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  
  // Payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  useEffect(() => {
    loadEvent();
  }, [id]);

  // Countdown timer
  useEffect(() => {
    if (!showCheckout || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error('Tiempo agotado. Tu reserva ha sido cancelada.');
          setShowCheckout(false);
          setShowPaymentForm(false);
          setSelectedTickets({});
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [showCheckout, navigate]);

  const loadEvent = async () => {
    try {
      const res = await getEvent(id);
      setEvent(res.data);
      const initial = {};
      res.data.ticket_types?.forEach(t => {
        initial[t.type_id] = 0;
      });
      setSelectedTickets(initial);
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Evento no encontrado');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const updateTicketQuantity = (typeId, delta) => {
    setSelectedTickets(prev => {
      const ticketType = event.ticket_types.find(t => t.type_id === typeId);
      const available = ticketType.quantity - ticketType.sold;
      const newQty = Math.max(0, Math.min(available, (prev[typeId] || 0) + delta));
      return { ...prev, [typeId]: newQty };
    });
  };

  const getTotalTickets = () => {
    return Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
  };

  const getSubtotal = () => {
    return event.ticket_types.reduce((sum, t) => {
      return sum + (t.price * (selectedTickets[t.type_id] || 0));
    }, 0);
  };

  const getFee = () => {
    return getSubtotal() * 0.05;
  };

  const getTotal = () => {
    return getSubtotal() + getFee();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPrice = (price) => {
    return `L. ${price.toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const startCheckout = () => {
    if (getTotalTickets() === 0) {
      toast.error('Selecciona al menos un ticket');
      return;
    }
    setShowCheckout(true);
    setTimeLeft(15 * 60); // Reset timer
  };

  const proceedToPayment = async () => {
    if (getTotalTickets() === 0) {
      toast.error('Selecciona al menos un ticket');
      return;
    }

    // Validate guest info if in guest mode
    if (checkoutMode === 'guest') {
      if (!guestEmail || !guestName) {
        toast.error('Por favor completa tu email y nombre');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
        toast.error('Email inválido');
        return;
      }
    }

    setCheckoutLoading(true);
    try {
      const tickets = Object.entries(selectedTickets)
        .filter(([_, qty]) => qty > 0)
        .map(([type_id, quantity]) => ({ type_id, quantity }));

      let res;
      if (checkoutMode === 'guest' || !isAuthenticated) {
        res = await createGuestOrder({
          event_id: event.event_id,
          tickets,
          payment_method: paymentMethod,
          guest_email: guestEmail,
          guest_name: guestName
        });
      } else {
        res = await createOrder({
          event_id: event.event_id,
          tickets,
          payment_method: paymentMethod
        });
      }

      // Handle free events
      if (res.data.tickets_created) {
        // Show success animation with confetti
        setShowPaymentSuccess(true);
        return;
      }

      // For paid events
      if (paymentMethod === 'stripe') {
        // Show inline payment form
        setCurrentOrderId(res.data.order_id);
        setShowPaymentForm(true);
      } else if (paymentMethod === 'paypal') {
        // PayPal redirects to external page
        if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        } else {
          toast.info('Redirigiendo a PayPal...');
          toast.error('PayPal no está disponible en este momento');
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    // Show success animation with confetti
    setShowPaymentSuccess(true);
  };

  const handlePaymentError = (error) => {
    toast.error(error || 'Error al procesar el pago');
  };

  const handleShare = async (platform = null) => {
    const shareUrl = window.location.href;
    const shareText = `¡Mira este evento: ${event.title}!`;
    
    if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
    } else {
      try {
        await navigator.share({
          title: event.title,
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copiado al portapapeles');
      }
    }
  };

  // Extract YouTube video ID
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="h-96 rounded-2xl bg-muted animate-pulse mb-8" />
          <div className="h-8 w-2/3 bg-muted animate-pulse rounded mb-4" />
          <div className="h-4 w-1/2 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (!event) return null;

  const eventDate = new Date(event.date);
  const isFree = event.is_free || event.ticket_types?.every(t => t.price === 0);
  const youtubeVideoId = getYoutubeVideoId(event.youtube_url);

  const defaultImages = {
    urbana: 'https://images.unsplash.com/photo-1567105800714-0106cfd63b55?w=1200&q=80',
    tropical: 'https://images.unsplash.com/photo-1504609813442-a8924e83f76e?w=1200&q=80',
    nightlife: 'https://images.unsplash.com/photo-1657054711964-a0eb014c2df1?w=1200&q=80',
    sports: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1200&q=80',
    conference: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80',
    food: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200&q=80',
    arts: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80',
    default: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=80'
  };

  const imageUrl = event.image_url || defaultImages[event.category] || defaultImages.default;

  // Get email for success screen
  const successEmail = isAuthenticated ? user?.email : guestEmail;

  return (
    <div className="min-h-screen pt-16">
      {/* Payment Success Animation */}
      {showPaymentSuccess && (
        <PaymentSuccess
          email={successEmail}
          ticketCount={getTotalTickets()}
          eventTitle={event.title}
          onContinue={() => navigate('/')}
          onViewTickets={isAuthenticated ? () => navigate('/tickets') : null}
        />
      )}

      {/* Hero Image */}
      <div className="relative h-[45vh] md:h-[55vh]">
        <img
          src={imageUrl}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        
        {/* Back button */}
        <Button
          variant="outline"
          className="absolute top-4 left-4 bg-white/90 dark:bg-card/90 backdrop-blur-sm border-0 shadow-lg hover:scale-105 transition-transform"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>

        {/* Share button with dropdown */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="absolute top-4 right-4 bg-white/90 dark:bg-card/90 backdrop-blur-sm border-0 shadow-lg hover:scale-105 transition-transform"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2" align="end">
            <div className="space-y-1">
              <button
                onClick={() => handleShare('whatsapp')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <MessageCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm">WhatsApp</span>
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <Facebook className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Facebook</span>
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <Twitter className="w-4 h-4 text-sky-500" />
                <span className="text-sm">Twitter</span>
              </button>
              <button
                onClick={() => handleShare()}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <Share2 className="w-4 h-4" />
                <span className="text-sm">Copiar link</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-24 relative z-10 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="animate-fade-in">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-primary/10 text-primary border-0">
                  {event.subcategory || event.category}
                </Badge>
                {event.status === 'published' && (
                  <Badge className="bg-green-500/10 text-green-600 border-0">En venta</Badge>
                )}
                {isFree && (
                  <Badge className="bg-orange-500/10 text-orange-600 border-0">GRATIS</Badge>
                )}
                {event.is_adult_only && (
                  <Badge className="bg-red-500/10 text-red-500 border-0 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    18+
                  </Badge>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="event-title">
                {event.title}
              </h1>
              
              {/* View Counter */}
              <div className="flex items-center gap-2 text-muted-foreground">
                <Eye className="w-4 h-4" />
                <span className="text-sm">{event.views || 0} visitas</span>
              </div>
            </div>

            {/* Adult Only Warning */}
            {event.is_adult_only && (
              <div className="glass-card p-4 border-2 border-red-500/30 bg-red-500/5 animate-fade-in">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-500">Evento Solo para Mayores de 18 Años</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Este evento está restringido para personas mayores de edad. 
                      Se solicitará identificación oficial en la entrada.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Event Details Card */}
            <div className="glass-card p-6 space-y-4 animate-slide-up">
              <div className="flex items-start gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {format(eventDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                  </p>
                  <p className="text-muted-foreground">
                    {format(eventDate, 'h:mm a')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <MapPin className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{event.location}</p>
                  <p className="text-muted-foreground">Ver en mapa</p>
                </div>
              </div>
            </div>

            {/* YouTube Video */}
            {youtubeVideoId && (
              <div className="glass-card p-6 animate-slide-up" style={{ animationDelay: '0.05s' }}>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-500" />
                  Video del Evento
                </h2>
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                    title="Video del evento"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>
            )}

            {/* Description */}
            <div className="glass-card p-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <h2 className="text-xl font-bold mb-4">Acerca del evento</h2>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {event.description}
              </p>
            </div>

            {/* Contact Organizer Button */}
            <div className="animate-slide-up" style={{ animationDelay: '0.15s' }}>
              <ContactOrganizerButton eventId={event.event_id} eventTitle={event.title} />
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="glass-card p-4 text-center hover:scale-105 transition-transform">
                <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium">Pago Seguro</p>
              </div>
              <div className="glass-card p-4 text-center hover:scale-105 transition-transform">
                <Zap className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Entrega Inmediata</p>
              </div>
              <div className="glass-card p-4 text-center hover:scale-105 transition-transform">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Validación QR</p>
              </div>
            </div>
          </div>

          {/* Ticket Selection */}
          <div className="lg:col-span-1">
            <div className="glass-card p-6 sticky top-24 animate-slide-up">
              {/* Countdown Timer */}
              {showCheckout && (
                <div className={`mb-4 p-3 rounded-xl flex items-center justify-center gap-2 ${
                  timeLeft < 60 ? 'bg-red-500/20 text-red-500' : 'bg-orange-500/20 text-orange-500'
                }`}>
                  <Clock className="w-5 h-5" />
                  <span className="font-bold text-lg">{formatTime(timeLeft)}</span>
                  <span className="text-sm">para completar tu compra</span>
                </div>
              )}

              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Selecciona tus Tickets
              </h2>

              <div className="space-y-4 mb-6">
                {event.ticket_types?.map((ticket, index) => {
                  const available = ticket.quantity - ticket.sold;
                  const selected = selectedTickets[ticket.type_id] || 0;
                  const isUnavailable = available === 0;

                  return (
                    <div
                      key={ticket.type_id}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                        isUnavailable 
                          ? 'opacity-50 border-border bg-muted/50' 
                          : selected > 0 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                      data-testid={`ticket-type-${ticket.type_id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{ticket.name}</p>
                          {ticket.description && (
                            <p className="text-xs text-muted-foreground mt-1">{ticket.description}</p>
                          )}
                        </div>
                        <p className="font-bold text-primary text-lg">
                          {ticket.price === 0 ? 'GRATIS' : formatPrice(ticket.price)}
                        </p>
                      </div>

                      <div className="flex justify-between items-center mt-3">
                        <p className="text-xs text-muted-foreground">
                          {isUnavailable ? '🔴 Agotado' : `✓ ${available} disponibles`}
                        </p>
                        
                        {!isUnavailable && !showPaymentForm && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 rounded-full"
                              onClick={() => updateTicketQuantity(ticket.type_id, -1)}
                              disabled={selected === 0}
                              data-testid={`decrease-${ticket.type_id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-bold text-lg">{selected}</span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 rounded-full"
                              onClick={() => updateTicketQuantity(ticket.type_id, 1)}
                              disabled={selected >= available}
                              data-testid={`increase-${ticket.type_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              {getTotalTickets() > 0 && (
                <div className="border-t border-border pt-4 mb-4 space-y-2 animate-fade-in">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatPrice(getSubtotal())}</span>
                  </div>
                  {!isFree && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Cargo por servicio (5%)</span>
                      <span>{formatPrice(getFee())}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-xl pt-2 border-t border-border">
                    <span>Total</span>
                    <span className="text-primary">{isFree ? 'GRATIS' : formatPrice(getTotal())}</span>
                  </div>
                </div>
              )}

              {/* Checkout Mode Selection (for non-authenticated users) */}
              {showCheckout && !isAuthenticated && getTotalTickets() > 0 && !checkoutMode && !showPaymentForm && (
                <div className="mb-4 space-y-3 animate-fade-in">
                  <p className="text-sm font-medium text-center mb-3">¿Cómo deseas continuar?</p>
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setCheckoutMode('guest')}
                    data-testid="checkout-guest"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Comprar como invitado
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => navigate('/register', { state: { from: `/events/${id}` } })}
                    data-testid="checkout-register"
                  >
                    <UserCircle className="w-4 h-4 mr-2" />
                    Crear cuenta
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => navigate('/login', { state: { from: `/events/${id}` } })}
                  >
                    Ya tengo cuenta
                  </Button>
                </div>
              )}

              {/* Guest Info Form */}
              {showCheckout && checkoutMode === 'guest' && getTotalTickets() > 0 && !showPaymentForm && (
                <div className="mb-4 space-y-3 animate-fade-in">
                  <div>
                    <Label htmlFor="guestName">Nombre Completo *</Label>
                    <Input
                      id="guestName"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Tu nombre completo"
                      className="mt-1"
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guestEmail">Correo Electrónico *</Label>
                    <Input
                      id="guestEmail"
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="mt-1"
                      data-testid="guest-email-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Recibirás tus tickets en este correo
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCheckoutMode(null)}
                  >
                    ← Volver
                  </Button>
                </div>
              )}

              {/* Payment Method Selection */}
              {showCheckout && getTotalTickets() > 0 && !isFree && (isAuthenticated || checkoutMode === 'guest') && !showPaymentForm && (
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Método de pago</p>
                  <div className="flex gap-2">
                    <Button
                      variant={paymentMethod === 'stripe' ? 'default' : 'outline'}
                      className={`flex-1 ${paymentMethod === 'stripe' ? 'bg-primary' : ''}`}
                      onClick={() => setPaymentMethod('stripe')}
                      data-testid="payment-stripe"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Tarjeta
                    </Button>
                    <Button
                      variant={paymentMethod === 'paypal' ? 'default' : 'outline'}
                      className={`flex-1 ${paymentMethod === 'paypal' ? 'bg-primary' : ''}`}
                      onClick={() => setPaymentMethod('paypal')}
                      data-testid="payment-paypal"
                    >
                      PayPal
                    </Button>
                  </div>
                </div>
              )}

              {/* Inline Payment Form */}
              {showPaymentForm && paymentMethod === 'stripe' && (
                <div className="mb-4 animate-fade-in">
                  <PaymentForm
                    orderId={currentOrderId}
                    amount={getTotal()}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => {
                      setShowPaymentForm(false);
                      setClientSecret(null);
                    }}
                  >
                    ← Cambiar método de pago
                  </Button>
                </div>
              )}

              {/* Main CTA Button */}
              {!showPaymentForm && (
                <>
                  {!showCheckout ? (
                    <Button
                      className="w-full btn-primary h-14 text-lg"
                      disabled={getTotalTickets() === 0}
                      onClick={startCheckout}
                      data-testid="start-checkout-button"
                    >
                      {getTotalTickets() === 0 ? (
                        'Selecciona tickets'
                      ) : isFree ? (
                        `Obtener ${getTotalTickets()} ticket${getTotalTickets() > 1 ? 's' : ''} gratis`
                      ) : (
                        `Continuar - ${formatPrice(getTotal())}`
                      )}
                    </Button>
                  ) : (
                    <Button
                      className="w-full btn-primary h-14 text-lg"
                      disabled={
                        getTotalTickets() === 0 || 
                        checkoutLoading || 
                        (!isAuthenticated && !checkoutMode) ||
                        (checkoutMode === 'guest' && (!guestEmail || !guestName))
                      }
                      onClick={proceedToPayment}
                      data-testid="checkout-button"
                    >
                      {checkoutLoading ? (
                        'Procesando...'
                      ) : isFree ? (
                        `Obtener ${getTotalTickets()} ticket${getTotalTickets() > 1 ? 's' : ''} gratis`
                      ) : paymentMethod === 'paypal' ? (
                        `Pagar con PayPal - ${formatPrice(getTotal())}`
                      ) : (
                        `Continuar al pago - ${formatPrice(getTotal())}`
                      )}
                    </Button>
                  )}
                </>
              )}

              <p className="text-xs text-center text-muted-foreground mt-4">
                Al comprar aceptas nuestros términos y condiciones
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
